import { DEVICE_ID } from './constants';
import type { CalendarEvent, Preferences, Reminder, Todo } from './types';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly payload?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Client-Type': 'pwa',
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof payload?.error === 'string' ? payload.error : `HTTP ${response.status}`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

const unwrap = async <T>(promise: Promise<{ data: T }>) => (await promise).data;

export const api = {
  sessionStatus: () => request<{ configured: boolean; authenticated: boolean }>('/session/status'),
  setupPassword: (setupToken: string, password: string) => request<{ success: true; expiresAt: number }>('/session/setup', {
    method: 'POST', body: JSON.stringify({ setupToken, password }),
  }),
  login: (password: string) => request<{ success: true; expiresAt: number }>('/session', {
    method: 'POST', body: JSON.stringify({ password }),
  }),
  logout: () => request<{ success: true }>('/session', { method: 'DELETE' }),

  listReminders: () => unwrap(request<{ data: Reminder[] }>(`/reminders?deviceId=${DEVICE_ID}`)),
  createReminder: (item: Partial<Reminder>) => unwrap(request<{ data: Reminder }>('/reminders', {
    method: 'POST', body: JSON.stringify({ ...item, deviceId: DEVICE_ID }),
  })),
  updateReminder: (item: Reminder) => unwrap(request<{ data: Reminder }>(`/reminders/${item.id}`, {
    method: 'PATCH', body: JSON.stringify({ ...item, deviceId: DEVICE_ID, revision: item.revision }),
  })),
  deleteReminder: (item: Reminder) => request(`/reminders/${item.id}`, {
    method: 'DELETE', body: JSON.stringify({ deviceId: DEVICE_ID, revision: item.revision }),
  }),

  listTodos: () => unwrap(request<{ data: Todo[] }>(`/todos?deviceId=${DEVICE_ID}`)),
  createTodo: (item: Partial<Todo>) => unwrap(request<{ data: Todo }>('/todos', {
    method: 'POST', body: JSON.stringify({ ...item, deviceId: DEVICE_ID }),
  })),
  updateTodo: (item: Todo) => unwrap(request<{ data: Todo }>(`/todos/${item.id}`, {
    method: 'PATCH', body: JSON.stringify({ ...item, deviceId: DEVICE_ID, revision: item.revision }),
  })),
  deleteTodo: (item: Todo) => request(`/todos/${item.id}`, {
    method: 'DELETE', body: JSON.stringify({ deviceId: DEVICE_ID, revision: item.revision }),
  }),

  listCalendarEvents: () => unwrap(request<{ data: CalendarEvent[] }>(`/calendar-events?deviceId=${DEVICE_ID}`)),
  createCalendarEvent: (item: Partial<CalendarEvent>) => unwrap(request<{ data: CalendarEvent }>('/calendar-events', {
    method: 'POST', body: JSON.stringify({ ...item, deviceId: DEVICE_ID }),
  })),
  updateCalendarEvent: (item: CalendarEvent) => unwrap(request<{ data: CalendarEvent }>(`/calendar-events/${item.id}`, {
    method: 'PATCH', body: JSON.stringify({ ...item, deviceId: DEVICE_ID, revision: item.revision }),
  })),
  deleteCalendarEvent: (item: CalendarEvent) => request(`/calendar-events/${item.id}`, {
    method: 'DELETE', body: JSON.stringify({ deviceId: DEVICE_ID, revision: item.revision }),
  }),

  getPreferences: () => unwrap(request<{ data: Preferences }>(`/preferences?deviceId=${DEVICE_ID}`)),
  savePreferences: (preferences: Pick<Preferences, 'themeId' | 'themeMode'>) =>
    unwrap(request<{ data: Preferences }>(`/preferences?deviceId=${DEVICE_ID}`, {
      method: 'PUT', body: JSON.stringify({ ...preferences, deviceId: DEVICE_ID }),
    })),
};
